// src/hooks/useSalesFilters.js
import { useState, useMemo } from "react";
import { format } from "date-fns";
import {
  getClient,
  getApartment,
  calculateRemainingBalance,
  checkOverduePayments,
  filterByDateRange,
  stripUnitPrefix,
} from "@/utils/salesHelpers";

/**
 * Manages all sales-tab state: search, status filter, date range,
 * and computes the filtered lists + export data.
 *
 * @param {Object} queries - { sales, clients, apartments, reservations, defaultedSales }
 */
export function useSalesFilters({
  sales,
  clients,
  apartments,
  reservations,
  defaultedSales,
}) {
  // --- Local state ---
  const [salesSearchQuery, setSalesSearchQuery] = useState("");
  const [salesStatusFilter, setSalesStatusFilter] = useState("all");
  const [salesDateRange, setSalesDateRange] = useState({
    type: "all",
    startDate: "",
    endDate: "",
  });
  const [isSalesFilterOpen, setIsSalesFilterOpen] = useState(false);

  // --- Quick-date helper ---
  const setSalesQuickDate = (type) => {
    setSalesDateRange({ type, startDate: "", endDate: "" });
    setIsSalesFilterOpen(false);
  };

  // --- Human-readable label for the date filter button ---
  const getSalesFilterLabel = () => {
    switch (salesDateRange.type) {
      case "all":
        return "All Time";
      case "thisWeek":
        return "This Week";
      case "lastWeek":
        return "Last Week";
      case "thisMonth":
        return "This Month";
      case "lastMonth":
        return "Last Month";
      case "custom":
        if (salesDateRange.startDate && salesDateRange.endDate) {
          return `${format(new Date(salesDateRange.startDate), "MMM d")} - ${format(
            new Date(salesDateRange.endDate),
            "MMM d, yyyy"
          )}`;
        }
        return "Custom Range";
      default:
        return "All Time";
    }
  };

  // --- Computed: active (non-defaulted) sales ---
  const activeSales = useMemo(
    () => sales.filter((sale) => sale.status !== "defaulted"),
    [sales]
  );

  // --- Computed: filtered sales for the main list ---
  const filteredSales = useMemo(() => {
    return filterByDateRange(activeSales, "sale_date", salesDateRange).filter((sale) => {
      const client = getClient(sale.client_id, clients);
      const apartment = getApartment(sale.apartment_id, apartments);
      const matchesSearch =
        client?.full_name
          ?.toLowerCase()
          .includes(salesSearchQuery.toLowerCase()) ||
        apartment?.unit_number
          ?.toLowerCase()
          .includes(stripUnitPrefix(salesSearchQuery).toLowerCase());
      const matchesStatus =
        salesStatusFilter === "all" || sale.status === salesStatusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [activeSales, salesDateRange, salesSearchQuery, salesStatusFilter, clients, apartments]);

  // --- Computed: filtered defaulted sales ---
  const filteredDefaultedSales = useMemo(() => {
    return defaultedSales.filter((sale) => {
      const client = getClient(sale.client_id, clients);
      const apartment = getApartment(sale.apartment_id, apartments);
      const matchesSearch =
        client?.full_name
          ?.toLowerCase()
          .includes(salesSearchQuery.toLowerCase()) ||
        apartment?.unit_number
          ?.toLowerCase()
          .includes(stripUnitPrefix(salesSearchQuery).toLowerCase());
      return matchesSearch;
    });
  }, [defaultedSales, salesSearchQuery, clients, apartments]);

  // --- Computed: reserved apartments (only when status filter = 'reserved') ---
  const filteredReservedApartments = useMemo(() => {
    if (salesStatusFilter !== "reserved") return [];
    return reservations.filter((reservation) => {
      const apartment = getApartment(reservation.apartment_id, apartments);
      return (
        reservation.status === "active" &&
        (reservation.non_client_name
          ?.toLowerCase()
          .includes(salesSearchQuery.toLowerCase()) ||
          apartment?.unit_number
            ?.toLowerCase()
            .includes(stripUnitPrefix(salesSearchQuery).toLowerCase()))
      );
    });
  }, [salesStatusFilter, reservations, salesSearchQuery, apartments]);

  // --- Computed: overdue sales ---
  const overdueSales = useMemo(() => {
    return activeSales.filter((sale) => {
      const overdueInfo = checkOverduePayments(sale);
      return overdueInfo?.isOverdue;
    });
  }, [activeSales]);

// --- Computed: export data for sales ---
const salesExportData = useMemo(() => {
  return filteredSales.map((sale) => {
    const client = getClient(sale.client_id, clients);
    const apartment = getApartment(sale.apartment_id, apartments);
    const remaining = calculateRemainingBalance(sale);

    return {
      sale_date: sale.sale_date,
      client: client?.full_name || "N/A",
      unit: apartment?.unit_number || "N/A",
      total_price: sale.total_price || 0,
      currency: sale.currency || "USD",
      contract_number: sale.contract_number || "-",
      advanced_payment: sale.first_installment || 0,
      monthly_payment: sale.monthly_payment || 0,
      contract_duration: sale.payment_duration_months || 0,
      remaining_balance: remaining,
      status: sale.status,
      notes: sale.notes || "",
    };
  });
}, [filteredSales, clients, apartments]);

// --- Static: export columns ---
const salesExportColumns = [
  { header: "Sale Date", key: "sale_date" },
  { header: "Client", key: "client" },
  { header: "Unit", key: "unit" },
  { header: "Unit Price", key: "total_price" },
  { header: "Currency", key: "currency" },
  { header: "Contract No.", key: "contract_number" },
  { header: "Advance", key: "advanced_payment" },
  { header: "Monthly Payment", key: "monthly_payment" },
  { header: "Contract Duration", key: "contract_duration" },
  { header: "Remaining Balance", key: "remaining_balance" },
  { header: "Sale Status", key: "status" },
  { header: "Notes", key: "notes" },
];

  return {
    // State
    salesSearchQuery,
    setSalesSearchQuery,
    salesStatusFilter,
    setSalesStatusFilter,
    salesDateRange,
    setSalesDateRange,
    isSalesFilterOpen,
    setIsSalesFilterOpen,
    // Helpers
    setSalesQuickDate,
    getSalesFilterLabel,
    // Computed lists
    activeSales,
    filteredSales,
    filteredDefaultedSales,
    filteredReservedApartments,
    overdueSales,
    // Export
    salesExportData,
    salesExportColumns,
  };
}